import { useEffect, useState } from 'react';

type ThemeMode = 'auto' | 'light' | 'dark';

const STORAGE_KEY = 'ta-theme';

function apply(mode: ThemeMode) {
  const root = document.documentElement;
  if (!root) return;
  if (mode === 'dark') {
    root.classList.add('dark');
    root.classList.remove('light');
  } else if (mode === 'light') {
    root.classList.remove('dark');
    root.classList.add('light');
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (prefersDark) {
      root.classList.add('dark');
      root.classList.remove('light');
    } else {
      root.classList.remove('dark');
      root.classList.add('light');
    }
  }
}

export function getInitialTheme(): ThemeMode {
  if (typeof window === 'undefined') return 'auto';
  const urlTheme = new URLSearchParams(window.location.search).get('theme') as ThemeMode | null;
  if (urlTheme === 'light' || urlTheme === 'dark' || urlTheme === 'auto') {
    return urlTheme;
  }
  const saved = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
  return saved ?? 'auto';
}

export function useTheme() {
  const [mode, setMode] = useState<ThemeMode>(() => getInitialTheme());

  useEffect(() => {
    apply(mode);
    window.localStorage.setItem(STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (mode !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => apply('auto');
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [mode]);

  function cycle() {
    const order: ThemeMode[] = ['auto', 'light', 'dark'];
    const next = order[(order.indexOf(mode) + 1) % order.length];
    setMode(next);
  }

  return { mode, setMode, cycle };
}

export function themeIcon(mode: ThemeMode) {
  if (mode === 'dark') return '🌙';
  if (mode === 'light') return '☀️';
  return '💻';
}

export function themeLabel(mode: ThemeMode) {
  if (mode === 'dark') return 'Dark';
  if (mode === 'light') return 'Light';
  return 'Auto';
}
